import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { SmartTagsFilter } from '~/components/Filters/SmartTagsFilter/SmartTagsFilter';
import { type SingleFilterProps } from '~/components/Filters/types';
import { PrivateSmartTagsSelect } from '~/components/SmartTagSelect/PrivateSmartTagsSelect';
import { type SmartTagBase } from '~/components/SmartTagSelect/utils';
import {
  clearTagsFilterValuesAction,
  setSmartTagsFilterLogicAction,
  setSmartTagsFiltersValueAction,
} from '~/store/filters/actions';
import {
  hasSmartTagsFiltersValuesSelector,
  selectedSmartTagsFilterLogicSelector,
  selectedSmartTagsFiltersSelector,
} from '~/store/filters/selectors';
import { type FilterLogicType } from '~/store/filters/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export const SingleSmartTagFilter = memo(({ onApplyClick }: SingleFilterProps) => {
  const dispatch = useDispatch();
  const hasValues = useAirSelector(hasSmartTagsFiltersValuesSelector);

  const clearValues = useCallback(() => {
    dispatch(clearTagsFilterValuesAction());
  }, [dispatch]);

  const selectedLogic = useSelector(selectedSmartTagsFilterLogicSelector);
  const selectedSmartTags = useSelector(selectedSmartTagsFiltersSelector);

  const onLogicChange = useCallback(
    (logic: FilterLogicType) => dispatch(setSmartTagsFilterLogicAction({ logic })),
    [dispatch],
  );

  const onSelectedSmartTagsChanged = useCallback(
    (smartTags: SmartTagBase[]) => {
      dispatch(setSmartTagsFiltersValueAction({ smartTags }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  return (
    <SingleFilterContainer
      hasValues={hasValues}
      currentFilter="smartTags"
      onClearClick={clearValues}
      onApplyClick={onApplyClick}
    >
      <SmartTagsFilter
        SmartTagsSelect={PrivateSmartTagsSelect}
        selectedLogic={selectedLogic}
        selectedSmartTags={selectedSmartTags}
        onLogicChange={onLogicChange}
        onSelectedSmartTagsChanged={onSelectedSmartTagsChanged}
      />
    </SingleFilterContainer>
  );
});

SingleSmartTagFilter.displayName = 'SingleSmartTagFilter';
